/**
 * Voice Chat Credentials Hook (REST API)
 *
 * Replaces WebSocket-based credential fetching with REST API.
 * Fetches voice chat credentials on-demand using REST API endpoint.
 */

import { useCallback, useState } from 'react';

import { CrewService } from '../services/CrewService';
import { VoiceChatConfig, VoiceChatCredentials } from '../types/voice-chat';

interface UseVoiceChatCredentialsOptions {
  config: VoiceChatConfig | null;
  onError?: (error: string | null) => void;
}

interface UseVoiceChatCredentialsReturn {
  connect: () => Promise<VoiceChatCredentials | null>;
  isConnecting: boolean;
  error: string | null;
}

export const useVoiceChatCredentials = (options: UseVoiceChatCredentialsOptions): UseVoiceChatCredentialsReturn => {
  const { config, onError } = options;
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (): Promise<VoiceChatCredentials | null> => {
    if (!config) {
      console.warn('[VoiceChat API] Cannot connect: config is null');
      return Promise.resolve(null);
    }

    setIsConnecting(true);
    setError(null);
    onError?.(null);

    try {
      const credentials = await CrewService.getVoiceChatCredentials(config.sessionId, config.participantId);

      if (!credentials) {
        throw new Error('Failed to get voice chat credentials');
      }

      setIsConnecting(false);
      return credentials;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch voice chat credentials';
      setError(errorMessage);
      setIsConnecting(false);
      onError?.(errorMessage);
      console.error('[VoiceChat API] Error fetching credentials:', err);
      throw err;
    }
  }, [config, onError]);

  return {
    connect,
    isConnecting,
    error
  };
};
